// src/components/Dashboard/DataIntegrationModeSelector.js - Enhanced (No Breaking Changes)
import React from 'react';
import { Button, Card, Row, Col, Badge } from 'react-bootstrap';

/**
 * Enhanced Component to toggle between different data integration modes
 * with subtle glassmorphism design (no breaking changes)
 */
const DataIntegrationModeSelector = ({
	currentMode,
	onSelectMode,
	plaidConnected,
	institution,
}) => {
	return (
		<Card className="bg-black border-0 mb-3 data-integration-mode-selector">
			<Card.Body className="p-0 bg-dark">
				<div className="mb-3">
					<p className="small text-white-50 mb-0">
						<i className="bi bi-info-circle me-2"></i>
						Choose your data source to get started. Switch between modes anytime to explore different scenarios.
					</p>
				</div>

				<Row className="g-3">
					{/* Plaid Integration Card */}
					<Col xs={12} md={6}>
						<Card
							className={`bg-dark mode-card h-100 position-relative ${currentMode === 'plaid' ? 'active' : ''}`}
							onClick={() => onSelectMode('plaid')}
							style={{ cursor: 'pointer' }}
						>
							{/* Active Indicator */}
							{currentMode === 'plaid' && (
								<div
									className="position-absolute top-0 start-0 w-100"
									style={{
										height: '3px',
										background: 'linear-gradient(90deg, #28a745, #32cd52)',
									}}
								/>
							)}

							<Card.Body className="p-4 bg-dark">
								<div className="d-flex align-items-start justify-content-between mb-3">
									<div className="d-flex align-items-center">
										<div
											className="rounded-3 p-3 me-3"
											style={{
												background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.2), rgba(40, 167, 69, 0.05))',
												border: '1px solid rgba(40, 167, 69, 0.3)'
											}}
										>
											<i className="bi bi-bank fs-4 text-success"></i>
										</div>
										<div>
											<h5 className="mb-1 text-white fw-bold">Plaid Integration</h5>
											<p className="text-white-50 small mb-0">Real banking data</p>
										</div>
									</div>
									{currentMode === 'plaid' && (
										<Badge bg="success" className="px-3 py-2">
											<i className="bi bi-check-circle me-1"></i>
											Active
										</Badge>
									)}
								</div>

								<p className="text-white-50 mb-3" style={{ fontSize: '0.9rem' }}>
									Connect to thousands of financial institutions through Plaid. Experience how our AI works with real banking data securely.
								</p>

								<div className="d-flex flex-wrap gap-2 mb-3">
									<Badge
										bg={plaidConnected ? 'success' : 'warning'}
										className="px-3 py-2"
									>
										<i className={`bi ${plaidConnected ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} me-1`}></i>
										{plaidConnected ? `Connected to ${institution}` : 'Not Connected'}
									</Badge>
									{plaidConnected && (
										<Badge bg="info" className="px-3 py-2">
											<i className="bi bi-shield-check me-1"></i>
											Bank-grade Security
										</Badge>
									)}
								</div>

								<ul className="list-unstyled small text-white-50 mb-0">
									<li className="mb-2">
										<i className="bi bi-check-circle text-success me-2"></i>
										Real-time account data
									</li>
									<li className="mb-2">
										<i className="bi bi-check-circle text-success me-2"></i>
										Automatic transaction sync
									</li>
									<li>
										<i className="bi bi-check-circle text-success me-2"></i>
										Secure credential handling
									</li>
								</ul>
							</Card.Body>

							<Card.Footer className="bg-dark border-top-0 pt-0 pb-4 px-4">
								<Button
									variant={currentMode === 'plaid' ? 'success' : 'outline-success'}
									className="w-100 py-2"
									onClick={(e) => {
										e.stopPropagation();
										onSelectMode('plaid');
									}}
								>
									{currentMode === 'plaid' ? (
										<>
											<i className="bi bi-check-lg me-2"></i>
											Currently Active
										</>
									) : (
										<>
											<i className="bi bi-bank me-2"></i>
											Switch to Plaid
										</>
									)}
								</Button>
							</Card.Footer>
						</Card>
					</Col>

					{/* Direct Data Integration Card */}
					<Col xs={12} md={6}>
						<Card
							className={`bg-dark mode-card h-100 position-relative ${currentMode === 'direct' ? 'active' : ''}`}
							onClick={() => onSelectMode('direct')}
							style={{ cursor: 'pointer' }}
						>
							{/* Active Indicator */}
							{currentMode === 'direct' && (
								<div
									className="position-absolute top-0 start-0 w-100"
									style={{
										height: '3px',
										background: 'linear-gradient(90deg, #17a2b8, #20c9e0)',
									}}
								/>
							)}

							<Card.Body className="bg-dark p-4">
								<div className="d-flex align-items-start justify-content-between mb-3">
									<div className="d-flex align-items-center">
										<div
											className="rounded-3 p-3 me-3"
											style={{
												background: 'linear-gradient(135deg, rgba(23, 162, 184, 0.2), rgba(23, 162, 184, 0.05))',
												border: '1px solid rgba(23, 162, 184, 0.3)'
											}}
										>
											<i className="bi bi-database fs-4 text-info"></i>
										</div>
										<div>
											<h5 className="mb-1 text-white fw-bold">Direct Data</h5>
											<p className="text-white-50 small mb-0">Simulated & customizable</p>
										</div>
									</div>
									{currentMode === 'direct' && (
										<Badge bg="success" className="px-3 py-2">
											<i className="bi bi-check-circle me-1"></i>
											Active
										</Badge>
									)}
								</div>

								<p className="text-white-50 mb-3" style={{ fontSize: '0.9rem' }}>
									Work with fully customizable simulated data. Perfect for testing, demos, and exploring our AI capabilities without connecting real accounts.
								</p>

								<div className="d-flex flex-wrap gap-2 mb-3">
									<Badge bg="info" className="px-3 py-2">
										<i className="bi bi-pencil-square me-1"></i>
										Fully Editable
									</Badge>
									<Badge bg="secondary" className="px-3 py-2">
										<i className="bi bi-lightning-fill me-1"></i>
										Instant Setup
									</Badge>
								</div>

								<ul className="list-unstyled small text-white-50 mb-0">
									<li className="mb-2">
										<i className="bi bi-check-circle text-info me-2"></i>
										Add/edit accounts & transactions
									</li>
									<li className="mb-2">
										<i className="bi bi-check-circle text-info me-2"></i>
										No bank connection required
									</li>
									<li>
										<i className="bi bi-check-circle text-info me-2"></i>
										Perfect for testing scenarios
									</li>
								</ul>
							</Card.Body>

							<Card.Footer className="bg-dark border-top-0 pt-0 pb-4 px-4">
								<Button
									variant={currentMode === 'direct' ? 'info' : 'outline-info'}
									className="w-100 py-2"
									onClick={(e) => {
										e.stopPropagation();
										onSelectMode('direct');
									}}
								>
									{currentMode === 'direct' ? (
										<>
											<i className="bi bi-check-lg me-2"></i>
											Currently Active
										</>
									) : (
										<>
											<i className="bi bi-database me-2"></i>
											Switch to Direct
										</>
									)}
								</Button>
							</Card.Footer>
						</Card>
					</Col>
				</Row>
			</Card.Body>
		</Card>
	);
};

export default DataIntegrationModeSelector;